import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useToast } from "@/hooks/use-toast";
import { Upload, Trash2, Copy, Search, ExternalLink, Folder, FolderOpen, ArrowLeft } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";

interface MediaFile {
  name: string;
  id: string;
  created_at: string;
  updated_at: string;
  metadata: Record<string, any> | null;
}

interface Folder {
  name: string;
  prefix: string;
}

export const MediaLibrary = () => {
  const [files, setFiles] = useState<MediaFile[]>([]);
  const [folders, setFolders] = useState<Folder[]>([]);
  const [currentFolder, setCurrentFolder] = useState<string>("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [showCreateFolder, setShowCreateFolder] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchFiles();
  }, []);

  const fetchFiles = async () => {
    try {
      setLoading(true);
      
      // Fetch files in current folder
      const { data: filesData, error: filesError } = await supabase.storage
        .from("flower-images")
        .list(currentFolder, {
          limit: 100,
          offset: 0,
        });

      if (filesError) throw filesError;

      // Separate files and folders
      const actualFiles = filesData?.filter(item => !item.name.endsWith('/')) || [];
      const folderPrefixes = filesData?.filter(item => item.name.endsWith('/')) || [];
      
      setFiles(actualFiles);
      setFolders(folderPrefixes.map(folder => ({
        name: folder.name.replace('/', ''),
        prefix: currentFolder ? `${currentFolder}${folder.name}` : folder.name
      })));

      // Create "Flowers" folder if it doesn't exist and we're in root
      if (!currentFolder) {
        const flowersExists = folderPrefixes.some(folder => folder.name === 'Flowers/');
        if (!flowersExists) {
          await createFlowersFolder();
        }
      }
    } catch (error) {
      console.error("Error fetching files:", error);
      toast({
        title: "Error",
        description: "Failed to fetch media files",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const createFlowersFolder = async () => {
    try {
      // Create a placeholder file to establish the folder structure
      const placeholder = new File([''], '.placeholder', { type: 'text/plain' });
      
      const { error } = await supabase.storage
        .from("flower-images")
        .upload('Flowers/.placeholder', placeholder);

      if (error) throw error;
      
      toast({
        title: "Folder Created",
        description: "Flowers folder has been created automatically",
      });
    } catch (error) {
      console.error("Error creating Flowers folder:", error);
    }
  };

  const createFolder = async () => {
    if (!newFolderName.trim()) return;

    try {
      // Create a placeholder file to establish the folder structure
      const placeholder = new File([''], '.placeholder', { type: 'text/plain' });
      const folderPath = currentFolder ? `${currentFolder}${newFolderName}/.placeholder` : `${newFolderName}/.placeholder`;
      
      const { error } = await supabase.storage
        .from("flower-images")
        .upload(folderPath, placeholder);

      if (error) throw error;
      
      toast({
        title: "Success",
        description: "Folder created successfully",
      });

      setNewFolderName("");
      setShowCreateFolder(false);
      fetchFiles();
    } catch (error) {
      console.error("Error creating folder:", error);
      toast({
        title: "Error",
        description: "Failed to create folder",
        variant: "destructive",
      });
    }
  };

  const navigateToFolder = (folderPrefix: string) => {
    setCurrentFolder(folderPrefix);
    setSearchTerm("");
  };

  const navigateUp = () => {
    const parentPath = currentFolder.split('/').slice(0, -2).join('/');
    setCurrentFolder(parentPath ? `${parentPath}/` : "");
    setSearchTerm("");
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid File",
        description: "Please select an image file",
        variant: "destructive",
      });
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Please select an image smaller than 5MB",
        variant: "destructive",
      });
      return;
    }

    setUploading(true);
    try {
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;
      const uploadPath = currentFolder ? `${currentFolder}${fileName}` : fileName;

      const { data, error } = await supabase.storage
        .from("flower-images")
        .upload(uploadPath, file);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Image uploaded successfully",
      });

      fetchFiles();
      
      // Reset file input
      const fileInput = document.getElementById('image-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (error) {
      console.error("Error uploading file:", error);
      toast({
        title: "Upload Failed",
        description: "Failed to upload image",
        variant: "destructive",
      });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (fileName: string) => {
    if (!confirm("Are you sure you want to delete this item?")) return;

    try {
      const fullPath = currentFolder ? `${currentFolder}${fileName}` : fileName;
      const { error } = await supabase.storage
        .from("flower-images")
        .remove([fullPath]);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Image deleted successfully",
      });

      fetchFiles();
    } catch (error) {
      console.error("Error deleting file:", error);
      toast({
        title: "Delete Failed",
        description: "Failed to delete image",
        variant: "destructive",
      });
    }
  };

  const getImageUrl = (fileName: string) => {
    const fullPath = currentFolder ? `${currentFolder}${fileName}` : fileName;
    return supabase.storage
      .from("flower-images")
      .getPublicUrl(fullPath).data.publicUrl;
  };

  const copyImageUrl = (fileName: string) => {
    const url = getImageUrl(fileName);
    navigator.clipboard.writeText(url);
    toast({
      title: "Copied",
      description: "Image URL copied to clipboard",
    });
  };

  const filteredFiles = files.filter(file =>
    file.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Media Library</CardTitle>
              <div className="flex items-center gap-2 mt-2">
                <span className="text-sm text-muted-foreground">Current folder:</span>
                <Badge variant="outline">
                  {currentFolder || "Root"}
                </Badge>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowCreateFolder(!showCreateFolder)}
            >
              <Folder className="h-4 w-4 mr-2" />
              New Folder
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <AlertDescription>
              Upload flower images to use in your catalog. Organized in folders for better management. 
              Supported formats: JPG, PNG, WebP. Max size: 5MB.
            </AlertDescription>
          </Alert>

          {showCreateFolder && (
            <Card className="bg-muted/50">
              <CardContent className="p-4">
                <div className="flex gap-2">
                  <Input
                    placeholder="Folder name..."
                    value={newFolderName}
                    onChange={(e) => setNewFolderName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && createFolder()}
                  />
                  <Button onClick={createFolder} disabled={!newFolderName.trim()}>
                    Create
                  </Button>
                  <Button variant="outline" onClick={() => setShowCreateFolder(false)}>
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          <div className="flex gap-4 items-center">
            <div className="flex-1">
              <div className="relative">
                <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search images..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            <div>
              <input
                id="image-upload"
                type="file"
                accept="image/*"
                onChange={handleFileUpload}
                className="hidden"
              />
              <Button
                onClick={() => document.getElementById('image-upload')?.click()}
                disabled={uploading}
              >
                <Upload className="h-4 w-4 mr-2" />
                {uploading ? "Uploading..." : "Upload Image"}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-8">Loading media files...</div>
      ) : (
        <div className="space-y-4">
          {/* Navigation */}
          {currentFolder && (
            <Card>
              <CardContent className="p-4">
                <Button variant="ghost" onClick={navigateUp} className="gap-2">
                  <ArrowLeft className="h-4 w-4" />
                  Back to parent folder
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Folders */}
          {folders.length > 0 && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {folders.map((folder) => (
                <Card key={folder.prefix} className="cursor-pointer hover:bg-muted/50" onClick={() => navigateToFolder(folder.prefix)}>
                  <CardContent className="p-4 text-center">
                    <FolderOpen className="h-12 w-12 mx-auto mb-2 text-primary" />
                    <div className="text-sm font-medium">{folder.name}</div>
                    <div className="text-xs text-muted-foreground">Folder</div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}

          {/* Files */}
          {filteredFiles.length > 0 && (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
              {filteredFiles.map((file) => (
                <Card key={file.id}>
                  <CardContent className="p-4">
                    <div className="aspect-square bg-muted rounded-lg overflow-hidden mb-3">
                      <img
                        src={getImageUrl(file.name)}
                        alt={file.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                    
                    <div className="space-y-2">
                      <div className="text-sm font-medium truncate" title={file.name}>
                        {file.name}
                      </div>
                      
                      <div className="text-xs text-muted-foreground">
                        {file.metadata?.size ? formatFileSize(file.metadata.size) : 'Unknown size'}
                      </div>
                      
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => copyImageUrl(file.name)}
                          title="Copy public URL"
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => window.open(getImageUrl(file.name), '_blank')}
                          title="Open in new tab"
                        >
                          <ExternalLink className="h-4 w-4" />
                        </Button>
                        
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(file.name)}
                          title="Delete"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {filteredFiles.length === 0 && folders.length === 0 && !loading && (
        <div className="text-center py-8 text-muted-foreground">
          {searchTerm ? "No items found matching your search." : "No items in this folder yet."}
        </div>
      )}
    </div>
  );
};